import { Injectable, inject } from '@angular/core';
import { IndexedDBStore } from './indexed-db.store';
import { LookupSearchCondition, LookupSearchConditionOperator } from './lookup-search-predicate';
import { LookupSearchPageResponse } from './lookup.types';

export type LookupWorkerConditionOperator = LookupSearchConditionOperator;
export type LookupWorkerCondition = LookupSearchCondition;

export interface LookupWorkerSearchConfig {
  tableName: string;
  primaryKey: string;
  chunkSize: number;
  rangeStrategy?: 'rowCount' | 'numericPrimaryKeyInterval';
  searchFields: string[];
  conditions?: LookupWorkerCondition[];
}

interface LookupRange {
  lowerBound?: IDBValidKey;
  upperBound?: IDBValidKey;
}

interface WorkerResult {
  type: 'result' | 'error';
  requestId: string;
  items?: any[];
  metrics?: WorkerSearchMetrics;
  error?: string;
}

interface WorkerSearchMetrics {
  lowerBound?: IDBValidKey;
  upperBound?: IDBValidKey;
  databaseOpenMs: number;
  scanMs: number;
  totalMs: number;
  rowsScanned: number;
  matchedRows: number;
  cancelled?: boolean;
}

interface ChunkSearchResult {
  items: any[];
  metrics?: WorkerSearchMetrics;
  roundTripMs: number;
}

interface LookupSearchWorkerState {
  workersByTable: Map<string, Worker[]>;
  rangesByTable: Map<string, LookupRange[]>;
  pendingInitialization: Map<string, Promise<LookupRange[]>>;
  latestRequestByTable: Map<string, string>;
  activeAbortByTable: Map<string, AbortController>;
}

/** Returned when a newer search for the same table started before this one finished. */
export const LOOKUP_SEARCH_SUPERSEDED = Symbol('lookup-search-superseded');

@Injectable({ providedIn: 'root' })
export class LookupSearchCoordinatorService {
  private readonly store = inject(IndexedDBStore);
  private requestSequence = 0;

  private static get global(): LookupSearchWorkerState {
    const key = '__LOOKUP_SEARCH_WORKER_STATE__';
    const globalStore = globalThis as typeof globalThis & Record<string, LookupSearchWorkerState | undefined>;
    if (!globalStore[key]) {
      globalStore[key] = {
        workersByTable: new Map(),
        rangesByTable: new Map(),
        pendingInitialization: new Map(),
        latestRequestByTable: new Map(),
        activeAbortByTable: new Map(),
      };
    }
    return globalStore[key]!;
  }

  /** Starts a table's shared worker pool once for the current browser-tab lifetime. */
  async warm(config: LookupWorkerSearchConfig): Promise<void> {
    if (typeof Worker === 'undefined' || typeof indexedDB === 'undefined') return;
    await this.getRanges(config);
  }

  /** Refreshes a table's key ranges after IndexedDB data changes without reloading existing workers. */
  async refreshRanges(config: LookupWorkerSearchConfig): Promise<void> {
    if (typeof Worker === 'undefined' || typeof indexedDB === 'undefined') return;

    const global = LookupSearchCoordinatorService.global;
    const pending = global.pendingInitialization.get(config.tableName);
    if (pending) await pending;

    const ranges = await this.buildRanges(config);
    const workers = global.workersByTable.get(config.tableName) ?? [];
    const workerUrl = new URL('assets/lookup-search.worker.js', document.baseURI);
    while (workers.length < ranges.length) {
      workers.push(new Worker(workerUrl, { type: 'module' }));
    }
    global.rangesByTable.set(config.tableName, ranges);
    global.workersByTable.set(config.tableName, workers);
  }

  async searchPage(
    config: LookupWorkerSearchConfig,
    query: string,
    pageSize: number,
    offset: number
  ): Promise<LookupSearchPageResponse<any> | typeof LOOKUP_SEARCH_SUPERSEDED | null> {
    if (typeof Worker === 'undefined' || typeof indexedDB === 'undefined') return null;

    const global = LookupSearchCoordinatorService.global;
    const requestId = `lookup-${Date.now()}-${++this.requestSequence}`;
    const activeAbortController = global.activeAbortByTable.get(config.tableName);
    if (activeAbortController) {
      activeAbortController.abort();
      global.workersByTable.get(config.tableName)?.forEach((worker) => worker.terminate());
      global.workersByTable.delete(config.tableName);
    }

    const abortController = new AbortController();
    global.activeAbortByTable.set(config.tableName, abortController);
    global.latestRequestByTable.set(config.tableName, requestId);

    try {
      const ranges = await this.getRanges(config);
      const workers = this.ensureWorkers(config.tableName, ranges);
      if (ranges.length === 0) return { items: [], hasMore: false, nextOffset: null };
      if (global.latestRequestByTable.get(config.tableName) !== requestId) return LOOKUP_SEARCH_SUPERSEDED;

      const resultLimit = offset + pageSize + 1;
      const resultSets = await Promise.all(
        ranges.map((range, index) => {
          const worker = workers[index];
          if (!worker) throw new Error(`Lookup search worker ${index} was not initialized`);
          return this.searchWorker(worker, abortController.signal, {
            type: 'search', requestId, tableName: config.tableName, ...range, query,
            searchFields: config.searchFields, conditions: config.conditions,
            limit: resultLimit,
          });
        })
      );
      if (global.latestRequestByTable.get(config.tableName) !== requestId) return LOOKUP_SEARCH_SUPERSEDED;

      const merged: any[] = resultSets.flatMap((result) => result.items ?? []);
      const pageWindow = merged.slice(offset, offset + pageSize + 1);
      const hasMore = pageWindow.length > pageSize;
      const items = hasMore ? pageWindow.slice(0, pageSize) : pageWindow;
      return { items, hasMore, nextOffset: hasMore ? offset + items.length : null };
    } catch (error) {
      if (abortController.signal.aborted) return LOOKUP_SEARCH_SUPERSEDED;
      this.invalidate(config.tableName);
      return null;
    } finally {
      if (global.activeAbortByTable.get(config.tableName) === abortController) {
        global.activeAbortByTable.delete(config.tableName);
      }
    }
  }

  invalidate(tableName?: string): void {
    const global = LookupSearchCoordinatorService.global;
    if (tableName) {
      global.rangesByTable.delete(tableName);
      global.workersByTable.get(tableName)?.forEach((worker) => worker.terminate());
      global.workersByTable.delete(tableName);
      global.pendingInitialization.delete(tableName);
      global.latestRequestByTable.delete(tableName);
      global.activeAbortByTable.get(tableName)?.abort();
      global.activeAbortByTable.delete(tableName);
      return;
    }
    global.rangesByTable.clear();
    global.workersByTable.forEach((workers) => workers.forEach((worker) => worker.terminate()));
    global.workersByTable.clear();
    global.pendingInitialization.clear();
    global.latestRequestByTable.clear();
    global.activeAbortByTable.forEach((abortController) => abortController.abort());
    global.activeAbortByTable.clear();
  }

  private async getRanges(config: LookupWorkerSearchConfig): Promise<LookupRange[]> {
    const global = LookupSearchCoordinatorService.global;
    const cachedRanges = global.rangesByTable.get(config.tableName);
    if (cachedRanges) {
      return cachedRanges;
    }

    const pending = global.pendingInitialization.get(config.tableName);
    if (pending) return pending;

    const initialization = (async () => {
      const ranges = await this.buildRanges(config);

      const workerUrl = new URL('assets/lookup-search.worker.js', document.baseURI);
      global.rangesByTable.set(config.tableName, ranges);
      global.workersByTable.set(config.tableName, ranges.map(() => new Worker(workerUrl, { type: 'module' })));
      return ranges;
    })();

    global.pendingInitialization.set(config.tableName, initialization);
    try {
      return await initialization;
    } finally {
      global.pendingInitialization.delete(config.tableName);
    }
  }

  private async buildRanges(config: LookupWorkerSearchConfig): Promise<LookupRange[]> {
    if (config.rangeStrategy === 'numericPrimaryKeyInterval') {
      const bounds = await this.store.getNumericPrimaryKeyBounds(config.tableName);
      if (!bounds) return [];

      const ranges: LookupRange[] = [];
      for (let lowerBound = bounds.min; lowerBound <= bounds.max; lowerBound += config.chunkSize) {
        ranges.push({
          lowerBound,
          upperBound: Math.min(lowerBound + config.chunkSize, bounds.max + 1),
        });
      }
      return ranges;
    }

    const boundaries = await this.store.getPrimaryKeyChunkBoundaries(config.tableName, config.chunkSize);
    const ranges: LookupRange[] = [];
    boundaries.forEach((upperBound, index) => {
      ranges.push({ lowerBound: index === 0 ? undefined : boundaries[index - 1], upperBound });
    });
    ranges.push({ lowerBound: boundaries[boundaries.length - 1] });
    return ranges;
  }

  private ensureWorkers(tableName: string, ranges: LookupRange[]): Worker[] {
    const global = LookupSearchCoordinatorService.global;
    const workers = global.workersByTable.get(tableName) ?? [];
    const workerUrl = new URL('assets/lookup-search.worker.js', document.baseURI);
    while (workers.length < ranges.length) {
      workers.push(new Worker(workerUrl, { type: 'module' }));
    }
    global.workersByTable.set(tableName, workers);
    return workers;
  }

  private searchWorker(
    worker: Worker,
    signal: AbortSignal,
    request: Record<string, unknown>
  ): Promise<ChunkSearchResult> {
    return new Promise((resolve, reject) => {
      const startedAt = performance.now();
      const handleMessage = (event: MessageEvent<WorkerResult>) => {
        if (event.data?.requestId !== request.requestId) return;
        cleanup();
        event.data.type === 'result'
          ? resolve({ items: event.data.items ?? [], metrics: event.data.metrics, roundTripMs: performance.now() - startedAt })
          : reject(new Error(event.data.error));
      };
      const handleError = (event: ErrorEvent) => {
        event.preventDefault();
        event.stopPropagation();
        cleanup();
        reject(event.error ?? new Error(event.message));
      };
      const handleAbort = () => {
        cleanup();
        resolve({
          items: [],
          roundTripMs: performance.now() - startedAt,
          metrics: { databaseOpenMs: 0, scanMs: 0, totalMs: 0, rowsScanned: 0, matchedRows: 0, cancelled: true },
        });
      };
      const cleanup = () => {
        worker.removeEventListener('message', handleMessage);
        worker.removeEventListener('error', handleError);
        signal.removeEventListener('abort', handleAbort);
      };
      worker.addEventListener('message', handleMessage);
      worker.addEventListener('error', handleError);
      signal.addEventListener('abort', handleAbort, { once: true });
      if (signal.aborted) {
        handleAbort();
        return;
      }
      worker.postMessage(request);
    });
  }
}